import type { AnswerRow, LeaderboardEntry, RankedLeaderboardEntry } from "./types";

// Deterministic leaderboard derivation from answer rows (avoids score drift).
// Ordering:
//   1. total points, descending
//   2. if points tie, average response duration on CORRECT answers only, ascending
//   3. if still tied, total response duration on CORRECT answers, ascending
//   4. if still exactly tied, players may share rank.
// Speed never becomes points. Incorrect/timed-out answers never affect the
// speed tiebreak. Subject sit-outs are excluded entirely.

export function aggregate(rows: AnswerRow[]): Map<string, LeaderboardEntry> {
  const map = new Map<string, LeaderboardEntry>();

  const ensure = (userId: string): LeaderboardEntry => {
    let e = map.get(userId);
    if (!e) {
      e = {
        user_id: userId,
        points: 0,
        correct_count: 0,
        attempted_count: 0,
        avg_correct_response_ms: null,
        total_correct_response_ms: 0,
      };
      map.set(userId, e);
    }
    return e;
  };

  for (const row of rows) {
    // Subject sit-outs are neutral and excluded from all counters.
    if (row.result === "subject_sitout") {
      ensure(row.user_id); // still ensure the player appears on the board
      continue;
    }
    const e = ensure(row.user_id);
    // "attempted" = playable questions the player was eligible for.
    e.attempted_count += 1;
    if (row.result === "correct") {
      e.points += row.points_awarded;
      e.correct_count += 1;
      if (row.response_duration_ms != null) {
        e.total_correct_response_ms += row.response_duration_ms;
      }
    }
  }

  for (const e of map.values()) {
    e.avg_correct_response_ms =
      e.correct_count > 0 ? e.total_correct_response_ms / e.correct_count : null;
  }

  return map;
}

// Compare two entries per the Season 0 ordering. Returns <0 if a ranks above b.
export function compareEntries(a: LeaderboardEntry, b: LeaderboardEntry): number {
  if (a.points !== b.points) return b.points - a.points; // more points first

  // A player with no correct answers has no speed metric; treat as "slowest".
  const aAvg = a.avg_correct_response_ms;
  const bAvg = b.avg_correct_response_ms;
  if (aAvg == null && bAvg == null) {
    // fall through to total (also null-ish) then stable
  } else if (aAvg == null) {
    return 1;
  } else if (bAvg == null) {
    return -1;
  } else if (aAvg !== bAvg) {
    return aAvg - bAvg; // faster average first
  }

  if (a.total_correct_response_ms !== b.total_correct_response_ms) {
    return a.total_correct_response_ms - b.total_correct_response_ms;
  }
  return 0; // exact tie -> shared rank
}

export function rankLeaderboard(entries: LeaderboardEntry[]): RankedLeaderboardEntry[] {
  const sorted = [...entries].sort(compareEntries);
  const ranked: RankedLeaderboardEntry[] = [];
  let lastRank = 0;
  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i];
    if (i === 0) {
      lastRank = 1;
    } else {
      const prev = sorted[i - 1];
      // Shared rank only when every ordering key is exactly equal.
      lastRank = compareEntries(prev, cur) === 0 ? lastRank : i + 1;
    }
    ranked.push({ ...cur, rank: lastRank });
  }
  return ranked;
}

export function leaderboardFromAnswers(rows: AnswerRow[]): RankedLeaderboardEntry[] {
  return rankLeaderboard([...aggregate(rows).values()]);
}
